"use client";

import Image from "next/image";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef } from "react";
import BlurFadeText from "@/components/common/blurFadeText";
import SectionEyebrow from "@/components/common/sectionEyebrow";
import SectionTitle from "@/components/common/sectionTitle";
import FAIcon from "@/components/icons/faIcon";

const EASE_OUT = [0.22, 1, 0.36, 1];

const CAPABILITY_CARDS = [
  {
    icon: "bolt",
    title: "Live notifications",
    description:
      "Highlight overdue actions and follow-ups before they’re missed.",
  },
  {
    icon: "calendar-check",
    title: "Scheduling control",
    description: "Manage checks, reviews and due dates from one place.",
  },
  {
    icon: "eye",
    title: "Clear visibility",
    description: "Spot operational drift before it becomes a wider issue.",
  },
  {
    icon: "envelope",
    title: "Escalation routes",
    description: "Keep communication moving when action is needed quickly.",
  },
  {
    icon: "file-contract",
    title: "Document linkage",
    description: "Attach forms, evidence and files to related activity.",
  },
  {
    icon: "shield-halved",
    title: "Structured control",
    description: "Maintain permissions, audit history and consistency.",
  },
  {
    icon: "chart-line",
    title: "Performance insights",
    description: "Review trends and operational performance clearly.",
  },
];

function revealProps(shouldReduceMotion, delay = 0, axis = "y", distance = 24) {
  if (shouldReduceMotion) {
    return {};
  }

  const hidden = { opacity: 0, filter: "blur(10px)" };

  if (axis === "x") {
    hidden.x = distance;
  } else if (axis === "-x") {
    hidden.x = -distance;
  } else {
    hidden.y = distance;
  }

  return {
    initial: hidden,
    whileInView: { opacity: 1, x: 0, y: 0, filter: "blur(0px)" },
    viewport: { once: true, amount: 0.22 },
    transition: { duration: 0.78, delay, ease: EASE_OUT },
  };
}

export function SlidingShowcaseCard({
  children,
  reverse = false,
  invert = false,
  priority = false,
  image = "/images/other/platform-screen-1.webp",
}) {
  const shouldReduceMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const imageSlide = useTransform(
    scrollYProgress,
    [0, 0.5],
    invert ? ["0%", "50%"] : reverse ? ["0%", "-50%"] : ["-50%", "0%"],
  );
  const imageScale = useTransform(scrollYProgress, [0, 0], [1.12, 1]);

  return (
    <section ref={sectionRef} className="relative">
      <div className="mx-auto flex max-w-328">
        <div className="relative flex w-full flex-col overflow-hidden rounded-lg bg-[#111315] lg:min-h-200">
          <div
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 28%, rgba(255,255,255,0.02) 28%, transparent 62%, rgba(255,255,255,0.04) 62%, transparent 100%)",
              backgroundSize: "8px 8px, 100% 100%",
            }}
          />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(254,201,80,0.14),transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.03),transparent_42%)]" />

          {}
          <div className="relative block w-full overflow-hidden lg:hidden">
            <div className="relative aspect-video w-full">
              <Image
                src={image}
                alt="SML Connect platform dashboard"
                fill
                className="object-cover object-top"
                priority={priority}
              />
            </div>
            <div className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-[#111315]" />
          </div>

          {}
          <motion.div
            style={
              shouldReduceMotion
                ? undefined
                : { x: imageSlide, scale: imageScale }
            }
            className={`absolute inset-4 sm:inset-8 lg:inset-12 z-20 overflow-hidden rounded-lg hidden lg:block ${
              invert ? "origin-right" : "origin-left"
            }`}
          >
            <div className="relative h-full w-full">
              <Image
                src={image}
                alt="SML Connect platform dashboard"
                fill
                sizes="(max-width: 1024px) 100vw, 68vw"
                className="object-cover object-top-left"
                priority={priority}
              />
            </div>
          </motion.div>

          <div className="relative z-0 grid flex-1 lg:grid-cols-2">
            <div
              className={`flex items-center px-6 py-6 sm:px-8 sm:py-12 lg:px-10 lg:py-14 ${
                reverse && !invert ? "lg:order-2" : ""
              }`}
            >
              <div className="max-w-lg space-y-6 text-white">{children}</div>
            </div>

            {}
            <div className="hidden lg:block" />
          </div>
        </div>
      </div>
    </section>
  );
}

function CapabilityCard({ card, index }) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.article
      {...revealProps(
        shouldReduceMotion,
        0.04 + index * 0.04,
        index % 3 === 0 ? "-x" : index % 3 === 2 ? "x" : "y",
        20,
      )}
      className=" transition-colors duration-300 flex lg:flex-col gap-6"
    >
      <div className="inline-flex pl-3 h-8 w-12 items-center justify-center rounded-[1rem] text-black">
        <FAIcon icon={card.icon} noMargin size={26} />
      </div>

      <div className="mt-2 ">
        <h3 className="border-l border-black/30 pl-5 font-raleway font-semibold leading-[0.98] tracking-tighter text-[#06040c] sm:text-[1.50rem]">
          {card.title}
        </h3>
        <p className="mt-3 pl-5 max-w-sm text-base leading-7 text-[#06040c]/62 sm:text-[1.02rem] sm:leading-6">
          {card.description}
        </p>
      </div>
    </motion.article>
  );
}

export function PlatformCapabilityGridSection() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section className="mx-auto w-full max-w-328 space-y-6 sm:space-y-8 -mt-10 lg:-mt-28">
      <motion.div
        {...revealProps(shouldReduceMotion, 0.02)}
        className="space-y-3"
      ></motion.div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 pt-10">
        {CAPABILITY_CARDS.map((card, index) => (
          <CapabilityCard key={card.title} card={card} index={index} />
        ))}
      </div>
    </section>
  );
}
